import { useEffect, useState } from "react";
import { Outlet, Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import { Search, Home, Heart, ListMusic, Clock, LogOut, User, Settings, Menu, X, Library } from "lucide-react";
import { useMediaAuth } from "../contexts/MediaAuthContext";
import MiniPlayer from "./MiniPlayer";
import AppSwitcher, { AppSwitcherMobile } from "../../my/components/AppSwitcher";

const NAV = [
  { to: "/media", icon: Home, label: "Início", end: true, testid: "media-nav-home" },
  { to: "/media/categoria/AU", icon: ListMusic, label: "Áudios", testid: "media-nav-audios" },
  { to: "/media/categoria/V2", icon: ListMusic, label: "Vídeos", testid: "media-nav-videos" },
  { to: "/media/pesquisa", icon: Library, label: "Catálogo", testid: "media-nav-catalogo" },
  { to: "/media/favoritos", icon: Heart, label: "Favoritos", testid: "media-nav-favorites" },
  { to: "/media/historico", icon: Clock, label: "Histórico", testid: "media-nav-history" },
  { to: "/media/playlists", icon: ListMusic, label: "Playlists", testid: "media-nav-playlists" },
];

export default function MediaLayout() {
  const { user, loading, logout } = useMediaAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [loc.pathname]);

  if (loading) return <div className="media-root grid place-items-center h-screen"><div className="live-dot" /></div>;

  // Rotas pessoais exigem sessão — visitante anónimo é reencaminhado para o
  // login preservando o destino (volta ao conteúdo depois de entrar). As
  // restantes rotas (início, catálogo, categorias, detalhe) são públicas.
  const PERSONAL_PATHS = ["/media/favoritos", "/media/historico", "/media/playlists", "/media/perfil", "/media/admin"];
  if (!user && PERSONAL_PATHS.some((p) => loc.pathname.startsWith(p))) {
    return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  }

  const isAdmin = user?.ID_Cliente === 1;
  const isConsultor = user?.ID_TipoCliente === "CS" || !!user?.IsConsultor;
  const loginHref = `/login?next=${encodeURIComponent(loc.pathname + loc.search)}`;
  const visibleNav = user
    ? NAV
    : NAV.filter((n) => !["/media/favoritos", "/media/historico", "/media/playlists"].includes(n.to));

  return (
    <div className="media-root">
      <div className="grain-overlay" />
      {user && <AppSwitcher current="media" isConsultor={isConsultor} isDBAdmin={!!user.IsDBAdmin} theme="dark" />}
      {user && <AppSwitcherMobile current="media" isConsultor={isConsultor} isDBAdmin={!!user.IsDBAdmin} theme="dark" />}
      <header
        data-testid="media-topbar"
        className={`fixed top-0 left-0 lg:left-[72px] right-0 z-40 transition-all duration-300 ${
          scrolled ? "bg-[#0F1829]/90 backdrop-blur-xl border-b border-white/5" : "bg-transparent"
        }`}
      >
        <div className="px-4 md:px-8 lg:px-12 py-4 flex items-center gap-8">
          <Link to="/media" className="flex items-center gap-2" data-testid="media-logo">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#D1CFC9] to-[#978D78] grid place-items-center">
              <span className="font-serif text-[#0F1829] text-lg font-semibold">I</span>
            </div>
            <span className="font-serif text-xl tracking-tight hidden sm:block">
              IHTP <span className="text-[#D1CFC9]">Media</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1 ml-4">
            {visibleNav.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                className={`px-3 py-2 text-sm rounded-full transition ${
                  loc.pathname === n.to || (n.end && loc.pathname === "/media")
                    ? "text-[#D1CFC9]"
                    : "text-slate-300 hover:text-white"
                }`}
              >
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="flex-1" />

          <button
            onClick={() => nav("/media/pesquisa")}
            data-testid="media-nav-search"
            className="p-2 rounded-full hover:bg-white/10 transition"
            aria-label="Pesquisar"
          >
            <Search size={20} />
          </button>

          <button
            onClick={() => setMenuOpen((v) => !v)}
            data-testid="media-nav-profile"
            className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full hover:bg-white/10"
          >
            {user ? (
              user.HasPhoto ? (
                <img
                  src={`${""}/api/profile/photo?uid=${user.ID_Cliente}`}
                  alt={user.NomeCompleto}
                  data-testid="media-avatar-img"
                  className="w-8 h-8 rounded-full object-cover border border-white/20"
                />
              ) : (
                <div data-testid="media-avatar-initial" className="w-8 h-8 rounded-full bg-gradient-to-br from-[#D1CFC9] to-[#978D78] grid place-items-center text-[#0F1829] font-semibold">
                  {(user.NomeCompleto || user.email || "?").charAt(0).toUpperCase()}
                </div>
              )
            ) : (
              <span data-testid="media-login-link" className="text-sm font-medium text-[#D1CFC9]">Entrar</span>
            )}
          </button>

          <button className="lg:hidden" onClick={() => setMenuOpen((v) => !v)} aria-label="Menu" data-testid="media-menu-toggle">
            {menuOpen ? <X /> : <Menu />}
          </button>
        </div>
      </header>

      {/* Mobile / profile dropdown */}
      {menuOpen && (
        <div className="fixed top-16 right-4 z-50 glass-panel rounded-2xl p-3 min-w-[220px]" data-testid="media-menu-dropdown">
          {user && (
            <div className="px-3 py-2 border-b border-white/5 mb-2">
              <p className="text-sm font-medium">{user.NomeCompleto || "Utilizador"}</p>
              <p className="text-xs text-slate-400 truncate">{user.Email || user.email}</p>
            </div>
          )}
          <div className="lg:hidden">
            {visibleNav.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/10 text-sm"
                data-testid={`${n.testid}-mobile`}
              >
                <n.icon size={16} /> {n.label}
              </Link>
            ))}
            <div className="h-px bg-white/5 my-2" />
          </div>
          {!user ? (
            <Link
              to={loginHref}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/10 text-sm text-[#D1CFC9]"
              data-testid="media-login-btn"
            >
              <User size={16} /> Entrar
            </Link>
          ) : (
            <>
              {isAdmin && (
                <Link to="/media/admin" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/10 text-sm" data-testid="media-nav-admin">
                  <Settings size={16} /> Admin
                </Link>
              )}
              <Link to="/media/perfil" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/10 text-sm" data-testid="media-nav-profile-page">
                <User size={16} /> Perfil
              </Link>
              <button
                onClick={async () => { await logout(); nav("/login"); }}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/10 text-sm text-red-400"
                data-testid="media-logout-btn"
              >
                <LogOut size={16} /> Terminar sessão
              </button>
            </>
          )}
        </div>
      )}

      <main className="pt-16 lg:pl-[72px] pb-[72px] lg:pb-0">
        <Outlet />
      </main>

      <MiniPlayer />
    </div>
  );
}
